import { useState, useEffect, useMemo } from 'react';
import { useMediaQuery } from '@mui/material';

import { BsPlusLg as Plus } from 'react-icons/bs';
import { BsArrowRight as Goto } from 'react-icons/bs';
import {
    HatGlasses,
    Search,
    Users,
    ShieldCheck,
    Settings,
    Mail,
    Phone,
    BriefcaseBusiness,
    Clock3,
    CheckCircle2,
    AlertTriangle,
    UserRound,
    ChevronRight
} from 'lucide-react';
import {
    MdVerified as Verified
} from 'react-icons/md';
import {
    TextField,
    Skeleton,
    Tooltip,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import { ReactComponent as ProfilePicture } from '@Assets/blankProfile.svg';
import InviteUsersView from '@CommonViews/InviteUsersView';
import MembersBuyView from '@CommonViews/MembersBuyView';
import BuyProUserSeats from '@CommonViews/BuyProUserSeats';
import PermissionsView from '@CommonViews/PermissionsView';
import UserInfoService from '@Helpers/URLHelpers/UserInfoService';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import OrgService from '@Helpers/URLHelpers/OrgService';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import UserSeatService from '@Helpers/URLHelpers/BillingService/UserSeatService';
import ErrorHandler from '@Root/ErrorHandler';
import './MembersPage.scss';

export default function MembersPage(props) {
    const [cursor, setCursor] = useState('auto');
    const [userDetailsLoader, setUserDetailsLoader] = useState(true);
    const [pageLoader, setPageLoader] = useState(true);
    const [org, setOrg] = useState({});
    const [orgUsers, setOrgUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState({});
    const [selectedUserPermissions, setSelectedUserPermissions] = useState({});
    const [permissions, setPermissions] = useState({});
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [showInviteUsersView, setShowInviteUsersView] = useState(false);
    const [showMembersBuyView, setShowMembersBuyView] = useState(false);
    const [showProUserSeat, setShowProUserSeat] = useState(false);
    const [showPermissionsView, setShowPermissionsView] = useState(false);
    const isMobile = useMediaQuery('(max-width: 991px)');
    const errorHandler = new ErrorHandler();
    const userAuthService = new UserAuthService();
    const userInfoService = new UserInfoService();
    const userSeatService = new UserSeatService();
    const orgService = new OrgService();

    const {
        currentOrg,
        currentOrgId,
        currentUserOrgPermissions
    } = useReduxOrg();

    const { activeUserId } = useReduxActiveUser();
    const { currentProject } = useReduxProject();

    const className = 'membersPage';

    useEffect(() => {
        renderData();
    }, [props, currentOrgId, activeUserId]);

    const renderData = async () => {
        setCursor('wait');
        setPageLoader(true);
        setUserDetailsLoader(true);

        try {
            let userPermissions =
                await userAuthService.getUserPermissionForParent(
                    activeUserId,
                    'org',
                    currentOrgId
                );

            userPermissions = JSON.parse(userPermissions.body);
            setPermissions(userPermissions);

            let userList =
                await userInfoService.getUserListByOrgId(currentOrgId);

            userList = JSON.parse(userList.body);

            setOrgUsers(userList || []);

            let currentOrgObj;

            if (!currentOrg || Object.keys(currentOrg).length === 0) {
                currentOrgObj =
                    await orgService.getOrgById(currentOrgId);

                currentOrgObj = JSON.parse(currentOrgObj.body);
            } else {
                currentOrgObj = currentOrg;
            }

            currentOrgObj.paymentType =
                currentOrgObj.paymentType || 'automatic';

            setOrg(currentOrgObj);

            if (userList?.length > 0) {
                await showUserDetails(userList[0].id);
            } else {
                setSelectedUser({});
                setSelectedUserPermissions({});
            }
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setPageLoader(false);
            setUserDetailsLoader(false);
            setCursor('auto');
        }
    };

    const showUserDetails = async (userId) => {
        setCursor('wait');
        setUserDetailsLoader(true);

        try {
            let userPermissions =
                await userAuthService.getUserPermissionForParent(
                    userId,
                    'org',
                    currentOrgId
                );

            userPermissions = JSON.parse(userPermissions.body);

            let userObj =
                await userInfoService.getUserById(userId);

            userObj = JSON.parse(userObj.body);

            setSelectedUser(userObj);
            setSelectedUserPermissions(userPermissions);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setUserDetailsLoader(false);
            setCursor('auto');
        }
    };

    const handleUserClick = (user) => {
        if (selectedUser?.id === user.id) {
            return;
        }

        setSelectedUser(user);
        showUserDetails(user.id);
    };

    const togglePermissionsView = () => {
        setShowPermissionsView(prev => !prev);
    };

    const toggleInviteUsersView = () => {
        setShowInviteUsersView(prev => !prev);
    };

    const toggleMembersBuyView = () => {
        setShowMembersBuyView(prev => !prev);
    };

    const toggleProUserSeat = () => {
        setShowProUserSeat(prev => !prev);
    };

    const filteredUsers = useMemo(() => {
        const query = userSearchQuery.trim().toLowerCase();

        if (!query) {
            return orgUsers;
        }

        return orgUsers.filter(user => {
            const name = user?.fullName || '';
            const username = user?.userName || '';
            const email = user?.email || '';

            return (
                name.toLowerCase().includes(query) ||
                username.toLowerCase().includes(query) ||
                email.toLowerCase().includes(query)
            );
        });
    }, [orgUsers, userSearchQuery]);

    const getUserSeatStatus = (user) => {
        if (user?.seatType === 'free') {
            return {
                type: 'free',
                text: 'Free',
                icon: <CheckCircle2 size={12} />
            };
        }

        if (!user?.expiryDateTimeStamp) {
            return {
                type: 'active',
                text: user?.seatType || 'Active',
                icon: <CheckCircle2 size={12} />
            };
        }

        const expiryDate = new Date(user.expiryDateTimeStamp);
        const currentDate = new Date();

        const timeDiff =
            expiryDate.getTime() - currentDate.getTime();

        const dayDiff = Math.ceil(
            timeDiff / (1000 * 3600 * 24)
        );

        if (dayDiff > 5) {
            return {
                type: 'active',
                text: user?.seatType || 'Active',
                icon: <CheckCircle2 size={12} />
            };
        }

        if (dayDiff > 0) {
            return {
                type: 'expiring',
                text: `${dayDiff}d left`,
                icon: <Clock3 size={12} />
            };
        }

        return {
            type: 'expired',
            text: 'Expired',
            icon: <AlertTriangle size={12} />
        };
    };

    const getUserRole = (user, userPermissions = {}) => {
        if (org?.creatorUserId === user?.id) {
            return {
                label: 'Owner',
                type: 'owner',
                icon: <Verified />
            };
        }

        if (userPermissions?.allowAdmin) {
            return {
                label: 'Admin',
                type: 'admin',
                icon: <ShieldCheck size={13} />
            };
        }

        if (userPermissions?.allowCreate) {
            return {
                label: 'Editor',
                type: 'editor',
                icon: <BriefcaseBusiness size={13} />
            };
        }

        if (userPermissions?.allowView) {
            return {
                label: 'Viewer',
                type: 'viewer',
                icon: <Users size={13} />
            };
        }

        return {
            label: 'Agent',
            type: 'agent',
            icon: <UserRound size={13} />
        };
    };

    const getInitials = (name = '') => {
        const words = name.trim().split(/\s+/);

        if (!words.length || !words[0]) {
            return '?';
        }

        if (words.length === 1) {
            return words[0].charAt(0).toUpperCase();
        }

        return (
            words[0].charAt(0) +
            words[words.length - 1].charAt(0)
        ).toUpperCase();
    };

    const avatarColors = [
        ['#E0ECFF', '#2457C5'],
        ['#E3F7ED', '#16834B'],
        ['#F4E9FF', '#7935B7'],
        ['#FFF0E1', '#C76516'],
        ['#FFE7EC', '#C53A59'],
        ['#E3F4F7', '#13778A']
    ];

    const getAvatarStyle = (name = '') => {
        const index =
            name
                .split('')
                .reduce(
                    (sum, char) => sum + char.charCodeAt(0),
                    0
                ) % avatarColors.length;

        const [background, color] = avatarColors[index];

        return {
            backgroundColor: background,
            color
        };
    };

    const dataURLtoFile = (dataurl) => {
        if (!dataurl || !dataurl.includes(',')) {
            return null;
        }

        try {
            const arr = dataurl.split(',');
            const mime =
                arr[0].match(/:(.*?);/)?.[1] || 'image/png';

            const bstr = atob(arr[1]);
            let n = bstr.length;

            const u8arr = new Uint8Array(n);

            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }

            return new File([u8arr], 'profile-image', {
                type: mime
            });
        } catch {
            return null;
        }
    };

    const getPhotoUrl = (photo) => {
        if (!photo) {
            return '';
        }

        if (typeof photo === 'string' && photo.startsWith('data:')) {
            const file = dataURLtoFile(photo);

            if (!file) {
                return '';
            }

            return URL.createObjectURL(file);
        }

        return photo;
    };

    const renderAvatar = (user, large = false) => {
        const photoUrl = getPhotoUrl(user?.photo);

        if (photoUrl) {
            return (
                <img
                    src={photoUrl}
                    alt={user?.fullName || 'Profile'}
                    className={
                        large
                            ? `${className}__profile__avatar__image`
                            : `${className}__user__avatar__image`
                    }
                />
            );
        }

        if (large) {
            return (
                <div
                    className={`${className}__profile__avatar__initials`}
                    style={getAvatarStyle(user?.fullName)}
                >
                    {getInitials(user?.fullName)}
                </div>
            );
        }

        return (
            <div
                className={`${className}__user__avatar__initials`}
                style={getAvatarStyle(user?.fullName)}
            >
                {getInitials(user?.fullName)}
            </div>
        );
    };

    const renderUserList = () => {
        if (!filteredUsers.length) {
            return (
                <div className={`${className}__sidebar__empty`}>
                    <Users size={30} />
                    <strong>
                        {userSearchQuery
                            ? 'No agents found'
                            : 'No agents available'}
                    </strong>
                    <span>
                        {userSearchQuery
                            ? 'Try another search term.'
                            : 'Invite an agent to get started.'}
                    </span>
                </div>
            );
        }

        return filteredUsers.map(user => {
            const isSelected =
                selectedUser?.id === user.id;

            const seatStatus =
                getUserSeatStatus(user);

            const isOwner =
                org?.creatorUserId === user.id;

            return (
                <button
                    key={user.id}
                    type="button"
                    className={`
                        ${className}__user
                        ${isSelected ? `${className}__user--active` : ''}
                    `}
                    onClick={() => handleUserClick(user)}
                >
                    <div className={`${className}__user__avatar`}>
                        {renderAvatar(user)}
                        <span
                            className={`
                                ${className}__user__presence
                                ${seatStatus.type === 'expired'
                                    ? `${className}__user__presence--offline`
                                    : ''}
                            `}
                        />
                    </div>

                    <div className={`${className}__user__body`}>
                        <div className={`${className}__user__top`}>
                            <span className={`${className}__user__name`}>
                                {user?.fullName || 'Unnamed Agent'}
                            </span>

                            {isOwner && (
                                <span className={`${className}__user__owner`}>
                                    Owner
                                </span>
                            )}
                        </div>

                        <span className={`${className}__user__username`}>
                            @{user?.userName || 'unknown'}
                        </span>

                        <span className={`${className}__user__email`}>
                            {user?.email || 'No email'}
                        </span>

                        <div className={`${className}__user__meta`}>
                            <span
                                className={`
                                    ${className}__seat
                                    ${className}__seat--${seatStatus.type}
                                `}
                            >
                                {seatStatus.icon}
                                {seatStatus.text}
                            </span>
                        </div>
                    </div>

                    <ChevronRight
                        size={16}
                        className={`${className}__user__arrow`}
                    />
                </button>
            );
        });
    };

    const renderProfile = () => {
        if (!selectedUser || Object.keys(selectedUser).length === 0) {
            return (
                <div className={`${className}__empty`}>
                    <div className={`${className}__empty__icon`}>
                        <Users size={30} />
                    </div>

                    <h2>No Agent Selected</h2>

                    <p>
                        Select an agent from the list to view
                        their profile and permissions.
                    </p>
                </div>
            );
        }

        const role = getUserRole(
            selectedUser,
            selectedUserPermissions
        );

        const seatStatus =
            getUserSeatStatus(selectedUser);

        const canManage =
            currentUserOrgPermissions?.allowAdmin ||
            String(activeUserId) === String(org?.ownerUserId);

        return (
            <>
                <div className={`${className}__profile`}>
                    <div className={`${className}__profile__cover`}>
                        <div className={`${className}__profile__coverPattern`} />
                    </div>

                    <div className={`${className}__profile__body`}>
                        <div className={`${className}__profile__top`}>
                            <div className={`${className}__profile__avatar`}>
                                {renderAvatar(selectedUser, true)}
                            </div>

                            <div className={`${className}__profile__actions`}>
                                {canManage && (
                                    <Tooltip
                                        title="Manage Permissions"
                                        arrow
                                    >
                                        <button
                                            type="button"
                                            className={`${className}__profile__settings`}
                                            onClick={togglePermissionsView}
                                        >
                                            <Settings size={17} />
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        <div className={`${className}__profile__identity`}>
                            <div className={`${className}__profile__nameRow`}>
                                <h1>
                                    {selectedUser?.fullName}
                                </h1>

                                <span
                                    className={`
                                        ${className}__role
                                        ${className}__role--${role.type}
                                    `}
                                >
                                    {role.icon}
                                    {role.label}
                                </span>
                            </div>

                            <p className={`${className}__profile__username`}>
                                @{selectedUser?.userName}
                            </p>

                            {selectedUser?.designation && (
                                <p className={`${className}__profile__designation`}>
                                    {selectedUser.designation}
                                </p>
                            )}
                        </div>

                        <div className={`${className}__profile__details`}>
                            <div className={`${className}__detail`}>
                                <div className={`${className}__detail__icon`}>
                                    <Mail size={16} />
                                </div>
                                <div>
                                    <span>Email</span>
                                    <strong>
                                        {selectedUser?.email || 'Not available'}
                                    </strong>
                                </div>
                            </div>

                            <div className={`${className}__detail`}>
                                <div className={`${className}__detail__icon`}>
                                    <Phone size={16} />
                                </div>
                                <div>
                                    <span>Contact</span>
                                    <strong>
                                        {selectedUser?.contactNumber
                                            ? `${selectedUser?.areaCode || ''} ${selectedUser.contactNumber}`
                                            : 'Not available'}
                                    </strong>
                                </div>
                            </div>

                            <div className={`${className}__detail`}>
                                <div className={`${className}__detail__icon`}>
                                    <BriefcaseBusiness size={16} />
                                </div>
                                <div>
                                    <span>Department</span>
                                    <strong>
                                        {selectedUser?.department || 'Not assigned'}
                                    </strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${className}__permissions`}>
                    <div className={`${className}__sectionHeader`}>
                        <div>
                            <span className={`${className}__sectionHeader__eyebrow`}>
                                ACCESS CONTROL
                            </span>
                            <h2>Permissions</h2>
                        </div>

                        {canManage && (
                            <button
                                type="button"
                                className={`${className}__editPermissions`}
                                onClick={togglePermissionsView}
                            >
                                <Settings size={14} />
                                Manage
                            </button>
                        )}
                    </div>

                    <div className={`${className}__permissionGrid`}>
                        <PermissionCard
                            className={className}
                            label="View"
                            enabled={
                                !!selectedUserPermissions?.allowView
                            }
                        />

                        <PermissionCard
                            className={className}
                            label="Create"
                            enabled={
                                !!selectedUserPermissions?.allowCreate
                            }
                        />

                        <PermissionCard
                            className={className}
                            label="Admin"
                            enabled={
                                !!selectedUserPermissions?.allowAdmin
                            }
                        />
                    </div>
                </div>
            </>
        );
    };

    return (
        <div
            className={className}
            style={{ cursor }}
        >
            <header className={`${className}__header`}>
                <div className={`${className}__header__identity`}>
                    <div className={`${className}__header__icon`}>
                        <HatGlasses size={19} />
                    </div>

                    <div>
                        <div className={`${className}__header__titleRow`}>
                            <h1>Agents</h1>

                            {!pageLoader && (
                                <span className={`${className}__header__count`}>
                                    {orgUsers.length}
                                </span>
                            )}
                        </div>

                        <p>
                            Manage your organization members
                            and access
                        </p>
                    </div>
                </div>

                <div className={`${className}__header__actions`}>
                    <div className={`${className}__search`}>
                        <Search size={17} />

                        <input
                            type="text"
                            placeholder="Search agents..."
                            value={userSearchQuery}
                            onChange={e =>
                                setUserSearchQuery(e.target.value)
                            }
                        />

                        {userSearchQuery && (
                            <button
                                type="button"
                                onClick={() => setUserSearchQuery('')}
                                className={`${className}__search__clear`}
                            >
                                ×
                            </button>
                        )}
                    </div>

                    {permissions?.allowAdmin && (
                        <Tooltip
                            title="Add agent"
                            arrow
                        >
                            <button
                                type="button"
                                className={`${className}__invite`}
                                onClick={toggleMembersBuyView}
                            >
                                <Plus size={16} />
                                <span>Invite Agent</span>
                            </button>
                        </Tooltip>
                    )}
                </div>
            </header>

            {pageLoader ? (
                <div className={`${className}__loading`}>
                    <div className={`${className}__loading__sidebar`}>
                        <Skeleton
                            variant="rounded"
                            height={90}
                        />

                        {[...Array(6)].map((_, index) => (
                            <Skeleton
                                key={index}
                                variant="rounded"
                                height={82}
                            />
                        ))}
                    </div>

                    <div className={`${className}__loading__main`}>
                        <Skeleton
                            variant="rounded"
                            height={280}
                        />

                        <Skeleton
                            variant="rounded"
                            height={220}
                        />
                    </div>
                </div>
            ) : (
                <main className={`${className}__container`}>
                    {!isMobile && (
                        <aside className={`${className}__sidebar`}>
                            <div className={`${className}__sidebar__summary`}>
                                <div>
                                    <span>TEAM</span>
                                    <strong>{orgUsers.length}</strong>
                                    <small>agents</small>
                                </div>

                                <div>
                                    <span>ADMINS</span>
                                    <strong>
                                        {
                                            orgUsers.filter(
                                                user =>
                                                    org?.creatorUserId === user.id
                                            ).length
                                        }
                                    </strong>
                                    <small>owners</small>
                                </div>
                            </div>

                            <div className={`${className}__sidebar__heading`}>
                                <span>ALL AGENTS</span>

                                <span>
                                    {filteredUsers.length}
                                </span>
                            </div>

                            <div className={`${className}__sidebar__list`}>
                                {renderUserList()}
                            </div>
                        </aside>
                    )}

                    <section className={`${className}__main`}>
                        {isMobile && (
                            <div className={`${className}__mobileSelect`}>
                                <FormControl
                                    fullWidth
                                    size="small"
                                >
                                    <InputLabel>
                                        Select Agent
                                    </InputLabel>

                                    <Select
                                        value={
                                            selectedUser?.id || ''
                                        }
                                        label="Select Agent"
                                        onChange={e => {
                                            const user =
                                                orgUsers.find(
                                                    item =>
                                                        item.id ===
                                                        e.target.value
                                                );

                                            if (user) {
                                                handleUserClick(user);
                                            }
                                        }}
                                    >
                                        {filteredUsers.map(user => (
                                            <MenuItem
                                                key={user.id}
                                                value={user.id}
                                            >
                                                {user.fullName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        )}

                        {userDetailsLoader ? (
                            <div className={`${className}__detailsLoading`}>
                                <Skeleton
                                    variant="rounded"
                                    height={300}
                                />
                                <Skeleton
                                    variant="rounded"
                                    height={220}
                                />
                            </div>
                        ) : (
                            renderProfile()
                        )}
                    </section>
                </main>
            )}

            {showPermissionsView && (
                <PermissionsView
                    open={showPermissionsView}
                    closePopup={togglePermissionsView}
                    user={selectedUser}
                    permissions={selectedUserPermissions}
                    activeUserPermissions={
                        currentUserOrgPermissions
                    }
                    project={currentProject}
                    org={org}
                    parentType="org"
                    type="user"
                    reloadData={renderData}
                />
            )}

            {showMembersBuyView && (
                <MembersBuyView
                    open={showMembersBuyView}
                    closePopup={toggleMembersBuyView}
                    toggleInviteUsersView={
                        toggleInviteUsersView
                    }
                    toggleProUserSeat={
                        toggleProUserSeat
                    }
                    org={org}
                />
            )}

            {showProUserSeat && (
                <BuyProUserSeats
                    open={showProUserSeat}
                    closePopup={toggleProUserSeat}
                    org={org}
                />
            )}

            {showInviteUsersView && (
                <InviteUsersView
                    open={showInviteUsersView}
                    closePopup={toggleInviteUsersView}
                    activeUserId={activeUserId}
                    org={org}
                    reloadData={renderData}
                />
            )}
        </div>
    );
}

function PermissionCard({
    className,
    label,
    enabled
}) {
    return (
        <div
            className={`
                ${className}__permission
                ${enabled
                    ? `${className}__permission--enabled`
                    : ''}
            `}
        >
            <div className={`${className}__permission__icon`}>
                {enabled ? (
                    <CheckCircle2 size={17} />
                ) : (
                    <AlertTriangle size={17} />
                )}
            </div>

            <div>
                <strong>{label}</strong>
                <span>
                    {enabled
                        ? 'Access enabled'
                        : 'Access disabled'}
                </span>
            </div>

            <div className={`${className}__permission__status`}>
                {enabled ? 'ON' : 'OFF'}
            </div>
        </div>
    );
}
